// Shared resolver for "what should the Preview button play for this voice".
//
// Instant-clone voices store several different audio paths:
//   - emotion_files.neutral — this voice's own generated neutral reference clip, from
//     /prepare_clone. The correct thing to preview once the clone has finished.
//   - audio_sample_path — the raw reference recording the user uploaded/recorded (an input,
//     not a preview — used only as ref_audio when the voice is actually used to synthesize
//     content).
//   - vox_audio_path — legacy: the TTS-generated sample from the old /clone-based instant-clone
//     flow (now dead, since handler.js only calls /prepare_clone), kept as a fallback for any
//     voice created before this migration.
// Design/Mix voices have no emotion_files/vox_audio_path — their audio_sample_path already IS
// the real generated output, so it's the correct (and only) thing to preview for those types.
//
// Callers may pass either the raw API/DB shape (audio_sample_path/vox_audio_path/emotion_files)
// or an already-transformed voice object (audio/previewAudio) — both are checked so this works
// everywhere a voice gets rendered, regardless of which shape that screen built.
export function getVoicePreviewPath(voice: any): string | null {
    if (!voice) return null;
    return (
        voice.previewAudio ||
        voice.emotion_files?.neutral ||
        voice.vox_audio_path ||
        voice.audio ||
        voice.audio_sample_path ||
        voice.preview_url ||
        voice.audio_url ||
        null
    );
}

// Resolves a voice straight to a playable URL. Paths are relative S3 keys and get prefixed
// with the audio bucket's base URL; anything already an absolute http(s) URL (e.g. a stock
// library voice preview) is returned unchanged.
export function getVoicePreviewUrl(voice: any): string | null {
    const raw = getVoicePreviewPath(voice);
    if (!raw) return null;
    if (/^https?:\/\//.test(raw)) return raw;

    const baseUrl = process.env.REACT_APP_CLONED_VOICE_BUCKET_URL || process.env.REACT_APP_AUDIO_PREVIEW_URL;
    return `${baseUrl}/${raw}`;
}

// Preview always plays the mp3 rendition of a voice sample, regardless of the
// extension the source path carries (some voices store .wav/.ogg samples). Swaps a
// trailing file extension for .mp3, or appends .mp3 when there is none. Returns the
// input unchanged when it is empty.
export function withMp3Extension(path: string): string {
    if (!path) return path;
    return path.replace(/\.[^./]+$/, '') + '.mp3';
}

// Duration to display alongside the Preview button — must match whichever file
// getVoicePreviewPath() resolves to, not the original reference recording's length.
// vox_audio_duration (instant-clone only) is the real length of the generated preview
// clip, which is often much shorter than the original upload's audio_duration once a
// short localized sentence replaces a long reference recording. Design/Mix voices have
// no vox_audio_duration; their audio_duration already describes the real output.
export function getVoicePreviewDuration(voice: any): number | null {
    if (!voice) return null;
    return voice.vox_audio_duration ?? voice.audio_duration ?? null;
}
